import type {
    Channel,
    EventMessage,
    Event,
    RoomParticipantActivity as PluginParticipantActivity,
} from '@pexip/plugin-api';

import {ParticipantActivities as PluginParticipantActivities} from '@pexip/plugin-api';

import {callSignals} from '../../signals/Call.signals';
import {infinityClientSignals} from '../../signals/InfinityClient.signals';
import type {Infinity} from '../../services/InfinityClient.service';
import type {InMeetingParticipant, RoomID} from '@pexip/media-components';
import {participantActivityBatchedSignal} from '../../signals/Participant.signals';
import {ParticipantActivities} from '../../types';

// TypeScript enums from different packages are nominally typed — they are not structurally compatible, even if their values are the same
const mapParticipantActivitiesToPluginType = (
    activity: Exclude<ParticipantActivities, ParticipantActivities.Transfer>,
): PluginParticipantActivities => {
    switch (activity) {
        case ParticipantActivities.Join:
            return PluginParticipantActivities.Join;
        case ParticipantActivities.Leave:
            return PluginParticipantActivities.Leave;
        case ParticipantActivities.Update:
            return PluginParticipantActivities.Update;
    }
};

export const registerInfinityClientSignals = (
    channels: Map<string, Channel>,
    infinity: Infinity,
) => {
    const forwardEvent = <T extends keyof EventMessage>(
        event: Omit<Event<T>, 'chanId'>,
    ) => {
        for (const channel of channels.values()) {
            channel.sendEvent(event);
        }
    };

    const getAllRoomParticipants = (roomId: RoomID) => {
        const meeting = infinity.getMeeting();
        const allRoomParticipants: InMeetingParticipant[] = [];
        for (const pId of meeting.getParticipants({roomId})) {
            const participant = meeting.getParticipant(pId, roomId);
            if (participant) {
                allRoomParticipants.push(participant);
            }
        }
        return allRoomParticipants;
    };

    return [
        participantActivityBatchedSignal.add(latestParticipantActivity => {
            const meeting = infinity.getMeeting();
            const roomIDs = new Set<RoomID>();
            const activitiesForPlugin: PluginParticipantActivity[] = [];

            for (const participantActivity of latestParticipantActivity) {
                const activity = participantActivity.activity;
                if (activity !== ParticipantActivities.Transfer) {
                    const getCorrectParticipant =
                        activity === ParticipantActivities.Leave
                            ? meeting.getDisconnectedParticipant
                            : meeting.getParticipant;

                    const roomParticipant = getCorrectParticipant(
                        participantActivity.participantId,
                        participantActivity.roomId,
                    );
                    if (roomParticipant) {
                        if (
                            activity === ParticipantActivities.Join ||
                            activity === ParticipantActivities.Leave
                        ) {
                            forwardEvent({
                                event:
                                    activity === ParticipantActivities.Join
                                        ? 'event:participantJoined'
                                        : 'event:participantLeft',
                                payload: {
                                    id: participantActivity.roomId,
                                    participant: roomParticipant,
                                },
                            });
                        }
                        activitiesForPlugin.push({
                            roomId: participantActivity.roomId,
                            activity: {
                                participant: roomParticipant,
                                type: mapParticipantActivitiesToPluginType(
                                    activity,
                                ),
                            },
                        });
                    }
                    if (!roomIDs.has(participantActivity.roomId)) {
                        roomIDs.add(participantActivity.roomId);
                        forwardEvent({
                            event: 'event:participants',
                            payload: {
                                id: participantActivity.roomId,
                                participants: getAllRoomParticipants(
                                    participantActivity.roomId,
                                ),
                            },
                        });
                    }
                }
            }
            forwardEvent({
                event: 'event:participantsActivities',
                payload: activitiesForPlugin,
            });
        }),
        infinityClientSignals.onRaiseHand.add(payload => {
            forwardEvent({
                event: 'event:raiseHand',
                payload,
            });
        }),
        infinityClientSignals.onConferenceStatus.add(payload => {
            forwardEvent({
                event: 'event:conferenceStatus',
                payload,
            });
        }),
        infinityClientSignals.onAuthenticatedWithConference.add(context => {
            forwardEvent({
                event: 'event:conference:authenticated',
                payload: context,
            });
        }),
        infinityClientSignals.onConnected.add(connected => {
            forwardEvent({
                event: 'event:connected',
                payload: connected,
            });
        }),
        infinityClientSignals.onDisconnected.add(disconnected => {
            forwardEvent({
                event: 'event:disconnected',
                payload: disconnected,
            });
        }),
        infinityClientSignals.onMe.add(payload => {
            forwardEvent({
                event: 'event:me',
                payload,
            });
        }),
        infinityClientSignals.onMessage.add(msg => {
            const {direct, ...payload} = msg;
            forwardEvent({
                event: direct ? 'event:directMessage' : 'event:message',
                payload,
            });
        }),
        infinityClientSignals.onApplicationMessage.add(applicationMessage => {
            forwardEvent({
                event: 'event:applicationMessage',
                payload: applicationMessage,
            });
        }),
        infinityClientSignals.onTransfer.add(({alias}) => {
            forwardEvent({
                event: 'event:transfer',
                payload: {alias},
            });
        }),
        infinityClientSignals.onCancelTransfer.add(cancelTransfer => {
            forwardEvent({
                event: 'event:cancelTransfer',
                payload: cancelTransfer,
            });
        }),
        infinityClientSignals.onStage.add(stage => {
            forwardEvent({
                event: 'event:stage',
                payload: stage,
            });
        }),
        callSignals.onPresentationConnectionChange.add(state => {
            forwardEvent({
                event: 'event:presentationConnectionStateChange',
                payload: state,
            });
        }),
        infinityClientSignals.onLayoutUpdate.add(layout => {
            forwardEvent({
                event: 'event:layoutUpdate',
                payload: layout,
            });
        }),
        infinityClientSignals.onBreakoutBegin.add(breakout => {
            forwardEvent({
                event: 'event:breakoutBegin',
                payload: breakout,
            });
        }),
        infinityClientSignals.onBreakoutEnd.add(breakout => {
            forwardEvent({
                event: 'event:breakoutEnd',
                payload: breakout,
            });
        }),
        infinityClientSignals.onBreakoutRefer.add(breakout => {
            forwardEvent({
                event: 'event:breakoutRefer',
                payload: breakout,
            });
        }),
    ];
};
